import React, { useEffect, useState, useCallback } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Loader from "../../components/common/Loader";
import { ArrowLeft, Search } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import { styles } from "../../styles/pages/Contact/ContactDetailsStyles";
import * as XLSX from "xlsx-js-style";
import { DownArrow, UpArrow } from "../../components/svg/Icons";
import RowsPerPageSelector from "../../components/common/RowsPerPageSelector";
import Pagination from "../../components/common/Pagination";
import { ReactComponent as DownloadIcon } from "../../icons/ExcelDownloadIcon.svg";
import { Tooltip } from "../../components/common/Tooltip";

// Define interfaces (reusing from ContactTable)
interface Service {
  service_name: string;
  price: string;
  service_GST: string;
  discount: string;
  service_total: string;
  description: string | null;
  discount_type: number;
}

interface Product {
  product_id: number;
  product_name: string;
  price: string;
  quantity: number;
  IGST: string;
  CGST: string;
  SGST: string;
  discount: string;
  product_total: string;
  description: string | null;
}

interface Quotation {
  id: string | number;
  quotation_number: string;
  quotation_type: number;
  quotation_subject: string;
  quotation_status: string;
  valid_until: string;
  terms_and_conditions: string | null;
  total: string;
  group_total: string;
  services?: Service[];
  products?: Product[];
  from_company_name?: string;
  from_address?: string;
  from_phone?: string;
  from_email?: string;
  to_client_name?: string;
  to_address?: string;
  to_email?: string;
}

interface Opportunity {
  opportunity_name: string;
  expected_close_date: string;
  opp_amount: string;
  probability: number;
  source: string;
  stage: string;
  status: string;
}

interface Contact {
  id: number;
  name: string;
  email: string;
  phone: string;
  company: string;
  status: string;
  converted_by: string;
  quotations: Quotation[];
  opportunities: Opportunity[];
}

const displayValue = (value: any): string => {
  if (value === null || value === undefined || value === "") return "N/A";
  return String(value);
};

const ContactDetails: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { contactId } = location.state || {};
  const [contact, setContact] = useState<Contact | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState<keyof Quotation | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const getAuthToken = (): string | null => {
    const token = sessionStorage.getItem("access_token");
    if (!token) {
      console.error("No access token found.");
      return null;
    }
    return token;
  };

  const axiosConfig = (token: string) => ({
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  const fetchContact = useCallback(async () => {
    if (!contactId) {
      setError("Contact ID not found.");
      setLoading(false);
      return;
    }

    try {
      const token = getAuthToken();
      if (!token) {
        setError("Unauthorized");
        setLoading(false);
        return;
      }
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/contact/list`,
        axiosConfig(token)
      );

      if (response.data.status === 200 && Array.isArray(response.data.data)) {
        const contactData = response.data.data.find(
          (c: any, index: number) => index + 1 === contactId
        );
        if (contactData) {
          setContact({
            id: contactId,
            name: contactData.name,
            email: contactData.email,
            phone: contactData.phone,
            company: contactData.company || "N/A",
            status: contactData.status,
            converted_by: contactData.converted_by,
            quotations: (contactData.quotations || []).map((q: any, index: number) => ({
              id: q.id || `temp-${index}`,
              quotation_number: q.quotation_number || "",
              quotation_type: q.quotation_type || 0,
              quotation_subject: q.quotation_subject || "",
              quotation_status: q.quotation_status || "",
              valid_until: q.valid_until || "",
              terms_and_conditions: q.terms_and_conditions || null,
              total: q.total || "0",
              group_total: q.group_total || "0",
              services: q.services || [],
              products: q.products || [],
              from_company_name: q.from_company_name || "",
              from_address: q.from_address || "",
              from_phone: q.from_phone || "",
              from_email: q.from_email || "",
              to_client_name: q.to_client_name || "",
              to_address: q.to_address || "",
              to_email: q.to_email || "",
            })),
            opportunities: contactData.opportunities || [],
          });
        } else {
          setError("Contact not found.");
        }
      } else {
        setError("No contacts found or invalid response.");
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => {
    fetchContact();
  }, [fetchContact]);

  useEffect(() => {
    setCurrentPage(1);
  }, [rowsPerPage]);

  const handleSort = (column: keyof Quotation) => {
    setSortColumn(column);
    setSortOrder(sortColumn === column && sortOrder === "asc" ? "desc" : "asc");
  };

  const filteredQuotations = contact?.quotations.filter((quotation) =>
    Object.values(quotation)
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  ) || [];

  const sortedQuotations = [...filteredQuotations].sort((a, b) => {
    if (!sortColumn) return 0;
    const valA = a[sortColumn];
    const valB = b[sortColumn];

    if (valA === undefined || valA === null) return 1;
    if (valB === undefined || valB === null) return -1;

    if (sortColumn === "group_total" || sortColumn === "total") {
      const numA = parseFloat(valA as string);
      const numB = parseFloat(valB as string);
      if (isNaN(numA) || isNaN(numB)) return 0;
      return sortOrder === "asc" ? numA - numB : numB - numA;
    }

    if (sortColumn === "valid_until") {
      const dateA = new Date(valA as string);
      const dateB = new Date(valB as string);
      if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) return 0;
      return sortOrder === "asc"
        ? dateA.getTime() - dateB.getTime()
        : dateB.getTime() - dateA.getTime();
    }

    const strA = (valA as string).toLowerCase();
    const strB = (valB as string).toLowerCase();
    return sortOrder === "asc"
      ? strA.localeCompare(strB)
      : strB.localeCompare(strA);
  });

  const isTabletView = window.innerWidth < 768;
  const displayedQuotations = isTabletView
    ? sortedQuotations
    : sortedQuotations.slice(
      (currentPage - 1) * rowsPerPage,
      currentPage * rowsPerPage
    );
  const totalPages = isTabletView
    ? 1
    : Math.ceil(filteredQuotations.length / rowsPerPage);

  const handleDownload = () => {
    const dataToDownload = searchTerm
      ? filteredQuotations
      : sortColumn
        ? sortedQuotations
        : contact?.quotations || [];

    if (!dataToDownload.length) {
      alert("No data available to download.");
      return;
    }

    const formattedData = dataToDownload.map((quotation) => ({
      "Quotation Number": quotation.quotation_number,
      "Quotation Type": quotation.quotation_type === 1 ? "Product" : "Service",
      "Quotation Status": quotation.quotation_status,
      "Quotation Subject": quotation.quotation_subject,
      "Valid Till": quotation.valid_until,
      "Group Total": quotation.group_total,
    }));

    const headers = Object.keys(formattedData[0]);
    const worksheet = XLSX.utils.aoa_to_sheet([]);

    XLSX.utils.sheet_add_aoa(worksheet, [["Quotation List"]], { origin: "A1" });

    worksheet["!merges"] = [
      {
        s: { r: 0, c: 0 },
        e: { r: 0, c: headers.length - 1 },
      },
    ];

    worksheet["A1"].s = {
      font: { bold: true, sz: 14 },
      alignment: { horizontal: "center", vertical: "center" },
    };

    XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: "A2" });

    headers.forEach((_, i) => {
      const col = String.fromCharCode(65 + i);
      const cell = `${col}2`;
      if (worksheet[cell]) {
        worksheet[cell].s = {
          font: { bold: true },
        };
      }
    });

    XLSX.utils.sheet_add_json(worksheet, formattedData, {
      origin: "A3",
      skipHeader: true,
    });

    worksheet["!cols"] = headers.map((key) => ({
      wch:
        Math.max(
          key.length,
          ...formattedData.map(
            (row) =>
              (row[key as keyof typeof row] as string)?.toString().length || 0
          ),
          "Quotation List".length
        ) + 2,
    }));

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Quotations");
    XLSX.writeFile(
      workbook,
      searchTerm ? `QuotationData_${searchTerm}.xlsx` : "QuotationData.xlsx"
    );
  };

  const handleViewClick = (quotationNumber: string) => {
    navigate("/quotation-view", {
      state: { quotationNumber, contactId },
    });
  };

  // Render product table for a quotation (unchanged)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderProductTable = (products: Product[], quotation: Quotation) => {
    const calculateProductTotals = (product: Product) => {
      const price = parseFloat(product.price) || 0;
      const qty = product.quantity || 1;
      const subtotal = price * qty;

      const discountStr = (product.discount || "0").trim();
      const discountType = discountStr.endsWith("%") ? 1 : 2;
      const discountValue =
        parseFloat(discountStr.replace(/[^0-9.]/g, "")) || 0;

      let discount = 0;
      if (discountType === 1) {
        discount = subtotal * (discountValue / 100);
      } else {
        discount = discountValue;
      }

      const taxableAmount = subtotal - discount;
      const sgstRate = parseFloat(product.SGST || "0");
      const cgstRate = parseFloat(product.CGST || "0");
      const igstRate = parseFloat(product.IGST || "0");
      const cgstAmount = taxableAmount * (cgstRate / 100);
      const sgstAmount = taxableAmount * (sgstRate / 100);
      const igstAmount = taxableAmount * (igstRate / 100);
      const totalGSTAmount =
        igstRate > 0 ? igstAmount : cgstAmount + sgstAmount;
      const total = taxableAmount + totalGSTAmount;

      return {
        subtotal,
        discount,
        taxableAmount,
        cgstAmount,
        sgstAmount,
        igstAmount,
        totalGSTAmount,
        total,
        discountType,
        discountValue,
        sgstRate,
        cgstRate,
        igstRate,
      };
    };

    const aggregatedTotals = products.map(calculateProductTotals);
    const totalPriceQty = aggregatedTotals.reduce(
      (sum, p) => sum + p.subtotal,
      0
    );
    const totalDiscount = aggregatedTotals.reduce(
      (sum, p) => sum + p.discount,
      0
    );
    const totalTaxable = totalPriceQty - totalDiscount;
    const totalSGST = aggregatedTotals.reduce(
      (sum, p) => sum + p.sgstAmount,
      0
    );
    const totalCGST = aggregatedTotals.reduce(
      (sum, p) => sum + p.cgstAmount,
      0
    );
    const totalIGST = aggregatedTotals.reduce(
      (sum, p) => sum + p.igstAmount,
      0
    );
    const totalGST = totalIGST > 0 ? totalIGST : totalCGST + totalSGST;
    const finalGroupTotal = totalTaxable + totalGST;

    const hasIGST = products.some(
      (product) => parseFloat(product.IGST || "0") > 0
    );

    return (
      <div className="mt-4">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
          Products
        </h4>
        <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
          <table className="w-full text-left text-gray-700 dark:text-gray-300">
            <thead
              className="text-xs uppercase bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
              style={{ fontSize: "14px" }}
            >
              <tr>
                <th className="px-4 py-2">Sr No.</th>
                <th className="px-4 py-2">Product ID</th>
                <th className="px-4 py-2">Product Name</th>
                <th className="px-4 py-2">Price</th>
                <th className="px-4 py-2">Qty</th>
                <th className="px-4 py-2">Discount</th>
                {hasIGST ? (
                  <th className="px-4 py-2">IGST</th>
                ) : (
                  <>
                    <th className="px-4 py-2">SGST</th>
                    <th className="px-4 py-2">CGST</th>
                  </>
                )}
                <th className="px-4 py-2">Total</th>
              </tr>
            </thead>
            <tbody style={{ fontSize: "12px" }}>
              {products.length > 0 ? (
                products.map((product, index) => {
                  const {
                    total,
                    discountType,
                    discountValue,
                    cgstRate,
                    sgstRate,
                    igstRate,
                  } = calculateProductTotals(product);
                  const discountDisplay =
                    discountType === 1
                      ? `${discountValue.toFixed(2)}%`
                      : `${discountValue.toFixed(2)}₹`;
                  return (
                    <tr
                      key={`product-${product.product_id}-${index}`}
                      className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                      <td className="px-4 py-2">{index + 1}</td>
                      <td className="px-4 py-2">{displayValue(product.product_id)}</td>
                      <td className="px-4 py-2">{displayValue(product.product_name)}</td>
                      <td className="px-4 py-2">
                        ₹
                        {parseFloat(product.price).toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td className="px-4 py-2">{product.quantity}</td>
                      <td className="px-4 py-2">{discountDisplay}</td>
                      {hasIGST ? (
                        <td className="px-4 py-2">{igstRate.toFixed(2)}%</td>
                      ) : (
                        <>
                          <td className="px-4 py-2">{sgstRate.toFixed(2)}%</td>
                          <td className="px-4 py-2">{cgstRate.toFixed(2)}%</td>
                        </>
                      )}
                      <td className="px-4 py-2">
                        ₹
                        {total.toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={hasIGST ? 8 : 9}
                    className="px-4 py-2 text-center text-gray-500 dark:text-gray-400"
                  >
                    No products available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="mt-4 text-right space-y-1">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Subtotal: ₹
            {totalPriceQty.toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Discount: ₹
            {totalDiscount.toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </p>
          {hasIGST ? (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              IGST: ₹
              {totalIGST.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </p>
          ) : (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                SGST: ₹
                {totalSGST.toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                CGST: ₹
                {totalCGST.toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </p>
            </>
          )}
          <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Group Total: ₹
            {finalGroupTotal.toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </p>
        </div>
      </div>
    );
  };

  // Render service table for a quotation (unchanged)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderServiceTable = (services: Service[], quotation: Quotation) => {
    const calculateServiceTotals = (service: Service) => {
      const price = parseFloat(service.price) || 0;
      const subtotal = price;

      const discountStr = (service.discount || "0").trim();
      const discountType =
        service.discount_type || (discountStr.endsWith("%") ? 1 : 2);
      const discountValue =
        parseFloat(discountStr.replace(/[^0-9.]/g, "")) || 0;

      let discount = 0;
      if (discountType === 1) {
        discount = subtotal * (discountValue / 100);
      } else {
        discount = discountValue;
      }

      const taxableAmount = subtotal - discount;
      const gstRate = parseFloat(service.service_GST || "0");
      const gstAmount = taxableAmount * (gstRate / 100);
      const total = taxableAmount + gstAmount;

      return {
        subtotal,
        discount,
        taxableAmount,
        gstAmount,
        total,
        discountType,
        discountValue,
        gstRate,
      };
    };

    const aggregatedTotals = services.map(calculateServiceTotals);
    const totalPrice = aggregatedTotals.reduce((sum, s) => sum + s.subtotal, 0);
    const totalDiscount = aggregatedTotals.reduce(
      (sum, s) => sum + s.discount,
      0
    );
    const totalTaxable = totalPrice - totalDiscount;
    const totalGST = aggregatedTotals.reduce((sum, s) => sum + s.gstAmount, 0);
    const finalGroupTotal = totalTaxable + totalGST;

    return (
      <div className="mt-4">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
          Services
        </h4>
        <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
          <table className="w-full text-left text-gray-700 dark:text-gray-300">
            <thead
              className="text-xs uppercase bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
              style={{ fontSize: "14px" }}
            >
              <tr>
                <th className="px-4 py-2">Sr No.</th>
                <th className="px-4 py-2">Service Name</th>
                <th className="px-4 py-2">Price</th>
                <th className="px-4 py-2">Discount</th>
                <th className="px-4 py-2">GST</th>
                <th className="px-4 py-2">Total</th>
              </tr>
            </thead>
            <tbody style={{ fontSize: "12px" }}>
              {services.length > 0 ? (
                services.map((service, index) => {
                  const { total, discountType, discountValue, gstRate } =
                    calculateServiceTotals(service);
                  const discountDisplay =
                    discountType === 1
                      ? `${discountValue.toFixed(2)}%`
                      : `${discountValue.toFixed(2)}₹`;
                  return (
                    <tr
                      key={`service-${index}`}
                      className="border-t border-b border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                      <td className="px-4 py-2">{index + 1}</td>
                      <td className="px-4 py-2">{displayValue(service.service_name)}</td>
                      <td className="px-4 py-2">
                        ₹
                        {parseFloat(service.price).toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                      <td className="px-4 py-2">{discountDisplay}</td>
                      <td className="px-4 py-2">{gstRate.toFixed(2)}%</td>
                      <td className="px-4 py-2">
                        ₹
                        {total.toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-2 text-center text-gray-500 dark:text-gray-400"
                  >
                    No services available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="mt-4 text-right space-y-1">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Subtotal: ₹
            {totalPrice.toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Discount: ₹
            {totalDiscount.toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            GST: ₹
            {totalGST.toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </p>
          <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Group Total: ₹
            {finalGroupTotal.toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </p>
        </div>
      </div>
    );
  };

  if (loading) return <Loader />;
  if (error) return <p className="text-center text-red-500">{error}</p>;
  if (!contact)
    return (
      <p className="text-center text-gray-500">
        No contact data available.
      </p>
    );

  return (
    <div
      className="max-w-7xl mx-auto text-gray-800 dark:text-gray-100"
      style={{
        fontFamily: "Lato, sans-serif",
        fontSize: "12px",
        fontWeight: "normal",
      }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=Lato:wght@400;700&display=swap"
        rel="stylesheet"
      />

      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
        <div className="flex items-center px-0 py-1 justify-between">
          <h3 className="text-theme-xl font-semibold text-gray-800 dark:text-white/90 text-center">
            Contact Details for {contact.name}
          </h3>
          <Link
            to="/contact-list"
            className="px-3 py-1.5 bg-[#2f6aa7] dark:bg-[#3d6794] hover:bg-[#2779bd] dark:hover:bg-[#335877] text-sm text-white dark:text-gray-300 rounded-lg flex items-center"
          >
            <ArrowLeft className="inline-block mr-1" size={16} />
            Back
          </Link>
        </div>

        {/* Card 1: Contact Information */}
        <div className="mt-6 bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100 border border-gray-300 dark:border-gray-700 shadow rounded-xl p-4 transition-colors">
          <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
            Contact Information
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <h6 className="text-sm text-gray-500 dark:text-gray-400">Name</h6>
              <p className="text-gray-800 dark:text-gray-100">{contact.name}</p>
            </div>
            <div>
              <h6 className="text-sm text-gray-500 dark:text-gray-400">Email</h6>
              <p className="text-gray-800 dark:text-gray-100">{contact.email}</p>
            </div>
            <div>
              <h6 className="text-sm text-gray-500 dark:text-gray-400">Mobile Number</h6>
              <p className="text-gray-800 dark:text-gray-100">{contact.phone}</p>
            </div>
            <div>
              <h6 className="text-sm text-gray-500 dark:text-gray-400">Company</h6>
              <p className="text-gray-800 dark:text-gray-100">{contact.company}</p>
            </div>
            <div>
              <h6 className="text-sm text-gray-500 dark:text-gray-400">Status</h6>
              <p className="text-gray-800 dark:text-gray-100">{contact.status}</p>
            </div>
            <div>
              <h6 className="text-sm text-gray-500 dark:text-gray-400">Converted By</h6>
              <p className="text-gray-800 dark:text-gray-100">{contact.converted_by}</p>
            </div>
          </div>
        </div>

        {/* Card 2: Opportunity Details */}
        <div className="mt-6 bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100 border border-gray-300 dark:border-gray-700 shadow rounded-xl p-4 transition-colors">
          <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
            Opportunity Details
          </h4>
          {contact.opportunities.length > 0 ? (
            <div className="space-y-4">
              {contact.opportunities.map((opportunity, index) => (
                <div key={index} className="border-b border-gray-200 dark:border-gray-600 pb-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <h6 className="text-sm text-gray-500 dark:text-gray-400">Opportunity Name</h6>
                      <p className="text-gray-800 dark:text-gray-100">{opportunity.opportunity_name}</p>
                    </div>
                    <div>
                      <h6 className="text-sm text-gray-500 dark:text-gray-400">Amount</h6>
                      <p className="text-gray-800 dark:text-gray-100">
                        ₹
                        {opportunity.opp_amount &&
                          !isNaN(parseFloat(opportunity.opp_amount))
                          ? parseFloat(opportunity.opp_amount).toLocaleString(
                            "en-IN",
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                          : "0.00"}
                      </p>
                    </div>
                    <div>
                      <h6 className="text-sm text-gray-500 dark:text-gray-400">Expected Close Date</h6>
                      <p className="text-gray-800 dark:text-gray-100">{opportunity.expected_close_date}</p>
                    </div>
                    <div>
                      <h6 className="text-sm text-gray-500 dark:text-gray-400">Probability</h6>
                      <p className="text-gray-800 dark:text-gray-100">{opportunity.probability}%</p>
                    </div>
                    <div>
                      <h6 className="text-sm text-gray-500 dark:text-gray-400">
                        Source
                      </h6>
                      <p className="text-gray-800 dark:text-gray-100">
                        {opportunity.source || "N/A"}
                      </p>
                    </div>
                    <div>
                      <h6 className="text-sm text-gray-500 dark:text-gray-400">
                        Stage
                      </h6>
                      <p className="text-gray-800 dark:text-gray-100">
                        {opportunity.stage || "N/A"}
                      </p>
                    </div>
                    <div>
                      <h6 className="text-sm text-gray-500 dark:text-gray-400">
                        Status
                      </h6>
                      <p className="text-gray-800 dark:text-gray-100">
                        {opportunity.status || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400">No opportunities available.</p>
          )}
        </div>

        {/* Card 3: Quotation Details */}
        <div className="mt-6 bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100 border border-gray-300 dark:border-gray-700 shadow rounded-xl p-4 transition-colors">
          <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">
            Quotation Details
          </h4>
          {contact.quotations.length > 0 ? (
            <div className={styles.tableCard}>
              <div className={styles.headerControls}>
                <RowsPerPageSelector
                  rowsPerPage={rowsPerPage}
                  setRowsPerPage={setRowsPerPage}
                />
                <div className={styles.controlsInner}>
                  <div className={styles.searchContainer}>
                    <Search className={styles.searchIcon} />
                    <input
                      type="text"
                      placeholder="Search..."
                      className={styles.searchInput}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <button
                    className={styles.downloadButton}
                    onClick={handleDownload}
                  >
                    Download
                    <DownloadIcon className={styles.downloadIcon} />
                  </button>
                </div>
              </div>
              {error && <p className={styles.errorText}>{error}</p>}

              <div className={styles.tableContainer}>
                <table className={styles.table}>
                  <thead>
                    <tr className={styles.theadRow}>
                      <th
                        className={styles.thName}
                        onClick={() => handleSort("quotation_number")}
                      >
                        <div className={styles.thSortable}>
                          <p className={styles.thText}>Number</p>
                          <button className={styles.sortButton}>
                            <DownArrow />
                            <UpArrow />
                          </button>
                        </div>
                      </th>
                      <th
                        className={styles.thTexts}
                        onClick={() => handleSort("quotation_type")}
                      >
                        <div className={styles.thSortable}>
                          <p className={styles.thText}>Type</p>
                          <button className={styles.sortButton}>
                            <DownArrow />
                            <UpArrow />
                          </button>
                        </div>
                      </th>
                      <th
                        className={styles.thTexts}
                        onClick={() => handleSort("quotation_status")}
                      >
                        <div className={styles.thSortable}>
                          <p className={styles.thText}>Status</p>
                          <button className={styles.sortButton}>
                            <DownArrow />
                            <UpArrow />
                          </button>
                        </div>
                      </th>
                      <th
                        className={styles.thTexts}
                        onClick={() => handleSort("quotation_subject")}
                      >
                        <div className={styles.thSortable}>
                          <p className={styles.thText}>Subject</p>
                          <button className={styles.sortButton}>
                            <DownArrow />
                            <UpArrow />
                          </button>
                        </div>
                      </th>
                      <th
                        className={styles.thTexts}
                        onClick={() => handleSort("valid_until")}
                      >
                        <div className={styles.thSortable}>
                          <p className={styles.thText}>Valid Till</p>
                          <button className={styles.sortButton}>
                            <DownArrow />
                            <UpArrow />
                          </button>
                        </div>
                      </th>
                      <th
                        className={styles.thName}
                        onClick={() => handleSort("group_total")}
                      >
                        <div className={styles.thSortable}>
                          <p className={styles.thText}>Group Total</p>
                          <button className={styles.sortButton}>
                            <DownArrow />
                            <UpArrow />
                          </button>
                        </div>
                      </th>
                      <th className={styles.thAction}>
                        <p className={styles.thText}>Action</p>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={7} className="text-center py-4">
                          <Loader />
                        </td>
                      </tr>
                    ) : displayedQuotations.length > 0 ? (
                      displayedQuotations.map((quotation, index) => (
                        <tr key={index} className={styles.tbodyRow}>
                          <td className={styles.tdName}>{quotation.quotation_number}</td>
                          <td className={styles.tdName}>
                            {quotation.quotation_type === 1 ? "Product" : "Service"}
                          </td>
                          <td className={styles.tdName}>{quotation.quotation_status}</td>
                          <td className={styles.tdName}>{quotation.quotation_subject}</td>
                          <td className={styles.tdName}>{quotation.valid_until}</td>
                          <td className={styles.tdName}>
                            ₹
                            {parseFloat(quotation.group_total).toLocaleString(
                              "en-IN",
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }
                            )}
                          </td>
                          <td className={styles.tdAction}>
                            <div className={styles.actionContainer}>
                              <Tooltip content="Quotation View" bgColor="bg-green-600">
                                <button
                                  onClick={() => handleViewClick(quotation.quotation_number)}
                                  className={styles.viewButton}
                                >
                                  <FontAwesomeIcon
                                    icon={faEye}
                                    className="text-white text-sm"
                                  />
                                </button>
                              </Tooltip>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7} className={styles.noDataText}>
                          No quotations available.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {!isTabletView && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  rowsPerPage={rowsPerPage}
                  filteredLength={filteredQuotations.length}
                  handlePageChange={setCurrentPage}
                />
              )}
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400">No quotations available.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactDetails;